/**
 * Utilidades para cálculo de progreso
 */

import type {
  Protocol,
  Layer,
  Frequency,
  DayProgress,
  LayerProgress,
  ProtocolGroup,
} from '@/core/types';
import { LAYER_ORDER } from '@/core/constants';

/**
 * Calcula el progreso de un protocolo individual
 */
export function calculateProtocolProgress(protocol: Protocol): number {
  const { subtasks } = protocol;
  if (subtasks.length === 0) return 0;

  const completed = subtasks.filter((s) => s.isDone).length;
  return Math.round((completed / subtasks.length) * 100);
}

/**
 * Calcula el progreso de una capa
 */
export function calculateLayerProgress(
  protocols: Protocol[],
  layer: Layer
): LayerProgress {
  const layerProtocols = protocols.filter(
    (p) => p.layer === layer && p.isActive
  );
  const total = layerProtocols.length;
  const completed = layerProtocols.filter((p) => p.isCompleted).length;

  // Una capa está desbloqueada si la anterior está completa
  const layerIndex = LAYER_ORDER.indexOf(layer);
  let isUnlocked = true;

  if (layerIndex > 0) {
    const previousLayer = LAYER_ORDER[layerIndex - 1];
    if (previousLayer) {
      const previousProgress = calculateLayerProgress(protocols, previousLayer);
      isUnlocked = previousProgress.isComplete;
    }
  }

  return {
    layer,
    total,
    completed,
    percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
    isUnlocked,
    isComplete: total > 0 && completed === total,
  };
}

/**
 * Calcula el progreso del día
 */
export function calculateDayProgress(
  protocols: Protocol[],
  frequency: Frequency = 'daily'
): DayProgress {
  const filtered = protocols.filter(
    (p) => p.frequency === frequency && p.isActive
  );

  const totalTasks = filtered.reduce((acc, p) => acc + p.subtasks.length, 0);
  const completedTasks = filtered.reduce(
    (acc, p) => acc + p.subtasks.filter((s) => s.isDone).length,
    0
  );
  const timeEstimated = filtered.reduce((acc, p) => acc + p.timeEstimate, 0);
  const timeSpent = filtered
    .filter((p) => p.isCompleted)
    .reduce((acc, p) => acc + p.timeEstimate, 0);

  const survivalComplete = calculateLayerProgress(filtered, 'survival').isComplete;
  const maintenanceComplete = calculateLayerProgress(filtered, 'maintenance').isComplete;
  const growthComplete = calculateLayerProgress(filtered, 'growth').isComplete;

  return {
    date: new Date().toISOString().split('T')[0] ?? '',
    survivalComplete,
    maintenanceComplete,
    growthComplete,
    totalTasks,
    completedTasks,
    timeSpent,
    timeEstimated,
    percentage: totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0,
  };
}

/**
 * Agrupa protocolos por capa
 */
export function groupByLayer(protocols: Protocol[]): ProtocolGroup[] {
  return LAYER_ORDER.map((layer) => {
    const layerProtocols = protocols.filter(
      (p) => p.layer === layer && p.isActive
    );
    const completedCount = layerProtocols.filter((p) => p.isCompleted).length;
    const totalTime = layerProtocols.reduce((acc, p) => acc + p.timeEstimate, 0);

    return {
      layer,
      protocols: layerProtocols,
      totalTime,
      completedCount,
      totalCount: layerProtocols.length,
      isFullyCompleted: layerProtocols.length > 0 && completedCount === layerProtocols.length,
    };
  });
}

/**
 * Filtra protocolos por frecuencia
 */
export function filterByFrequency(
  protocols: Protocol[],
  frequency: Frequency
): Protocol[] {
  return protocols.filter((p) => p.frequency === frequency && p.isActive);
}

/**
 * Obtiene el tiempo total estimado para una lista de protocolos
 */
export function getTotalTime(protocols: Protocol[]): number {
  return protocols.reduce((acc, p) => acc + p.timeEstimate, 0);
}

/**
 * Obtiene el tiempo completado para una lista de protocolos
 */
export function getCompletedTime(protocols: Protocol[]): number {
  return protocols
    .filter((p) => p.isCompleted)
    .reduce((acc, p) => acc + p.timeEstimate, 0);
}

/**
 * Calcula la racha (streak) basada en historial
 */
export function calculateStreak(
  completionDates: string[],
  today: Date = new Date()
): number {
  if (completionDates.length === 0) return 0;

  // Ordenar fechas de más reciente a más antigua
  const sorted = [...completionDates].sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  );

  let streak = 0;
  const currentDate = new Date(today);
  currentDate.setHours(0, 0, 0, 0);

  for (const dateStr of sorted) {
    const date = new Date(dateStr);
    date.setHours(0, 0, 0, 0);

    const diffDays = Math.floor(
      (currentDate.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays === streak) {
      streak++;
    } else if (diffDays > streak) {
      break;
    }
  }

  return streak;
}
